import React from "react";
import { Link } from "react-router-dom";

const STATS = [
  { num: "10,000+", label: "Happy customers" },
  { num: "500+", label: "Verified brands" },
  { num: "4.9★", label: "Average rating" },
  { num: "30s", label: "Average delivery" },
];

const VALUES = [
  {
    icon: "⚡",
    title: "Instant Delivery",
    desc: "Codes hit your inbox within seconds — no waiting, no friction.",
  },
  {
    icon: "🛡",
    title: "Verified & Secure",
    desc: "PCI-compliant 3D-secure payments and fraud-monitored orders.",
  },
  {
    icon: "🤝",
    title: "Authorized Channels",
    desc: "Codes sourced exclusively from official, vetted distributors.",
  },
  {
    icon: "💬",
    title: "Direct Support",
    desc: "Reach us on WhatsApp or by email whenever you need a hand.",
  },
];

const TRUST_CARDS = [
  {
    title: "Why Choose Us",
    items: [
      "UAE registered business",
      "Secure card payments",
      "Anti-fraud systems",
      "Fast digital delivery",
      "Dedicated support team",
      "Verified suppliers",
    ],
  },
  {
    title: "Corporate Orders",
    items: [
      "Employee rewards",
      "Bulk gift card programs",
      "Promotions & campaigns",
      "Channel-partner incentives",
      "Custom branded delivery",
      "Volume discounts",
    ],
  },
  {
    title: "Supplier Standards",
    body:
      "We source digital products through authorized and vetted distribution channels, ensuring reliability, compliance and a trusted experience for every purchase. Every code is traceable to its origin.",
  },
];

const About = () => {
  return (
    <section className="gpa-section">
      <div className="gpa-bg-orb gpa-bg-orb-tl" />
      <div className="gpa-bg-orb gpa-bg-orb-br" />
      <div className="gpa-bg-grid" aria-hidden="true" />

      <div className="gpa-inner">
        {/* HERO BRAND BLOCK */}
        <div className="gpa-hero">
          <div className="gpa-hero-text">
            <div className="gpa-eyebrow">
              <span className="gpa-eyebrow-dot" />
              ABOUT GLOBALGIFTPASS · EST. UAE
            </div>
            <h1 className="gpa-title">
              The premium destination for{" "}
              <span className="gpa-title-accent">digital gift cards.</span>
            </h1>
            <p className="gpa-lede">
              GlobalGiftPass is a UAE-registered marketplace for eGifts and
              gift cards from the brands you actually want — Amazon, Noon,
              Apple, Netflix, PlayStation, Google Play, Uber, Xbox and 500+
              more. Instant delivery, verified codes, and dedicated human
              support behind every order.
            </p>
            <p className="gpa-meta-line">
              <span aria-hidden="true">⚖️</span> Owned and operated by{" "}
              <strong>Ventureworks Dynamics LLC</strong> · registered in
              Sharjah Media City Free Zone, UAE · Trade License No. 243067201
            </p>

            <div className="gpa-hero-cta-row">
              <Link to="/shop" className="gpa-cta-primary">
                Shop Gift Cards
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
              <Link to="/contact" className="gpa-cta-secondary">
                Talk to our team
              </Link>
            </div>
          </div>

          <div className="gpa-hero-visual">
            <div className="gpa-hero-image-wrap">
              <img
                src="https://globalgiftpass-images.s3.ap-northeast-2.amazonaws.com/1737542661337_160715236.png"
                alt="GlobalGiftPass — selection of premium gift cards"
              />
              <div className="gpa-hero-image-shine" aria-hidden="true" />
            </div>
            <div className="gpa-hero-tag gpa-hero-tag-tl">
              <span className="gpa-hero-tag-num">500+</span>
              <span className="gpa-hero-tag-label">Verified Brands</span>
            </div>
            <div className="gpa-hero-tag gpa-hero-tag-br">
              <span className="gpa-hero-tag-num">4.9★</span>
              <span className="gpa-hero-tag-label">Customer Rating</span>
            </div>
          </div>
        </div>

        {/* STATS STRIP */}
        <div className="gpa-stats">
          {STATS.map((s) => (
            <div key={s.label} className="gpa-stat">
              <div className="gpa-stat-num">{s.num}</div>
              <div className="gpa-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* OUR VALUES */}
        <div className="gpa-block">
          <div className="gpa-block-head">
            <div className="gpa-block-eyebrow">OUR PROMISE</div>
            <h2 className="gpa-block-title">
              What you get with{" "}
              <span className="gpa-block-title-accent">every order</span>
            </h2>
            <p className="gpa-block-sub">
              The same standards we'd expect ourselves — fast, secure, and
              backed by people who actually answer the phone.
            </p>
          </div>

          <div className="gpa-values">
            {VALUES.map((v) => (
              <div key={v.title} className="gpa-value">
                <div className="gpa-value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* TRUST CARDS */}
        <div className="gpa-block">
          <div className="gpa-block-head">
            <div className="gpa-block-eyebrow">HOW WE OPERATE</div>
            <h2 className="gpa-block-title">
              Built on <span className="gpa-block-title-accent">trust</span>
            </h2>
          </div>

          <div className="gpa-trust-grid">
            {TRUST_CARDS.map((c) => (
              <div key={c.title} className="gpa-trust-card">
                <div className="gpa-trust-card-head">
                  <h3>{c.title}</h3>
                  <span className="gpa-trust-card-hint" aria-hidden="true">★</span>
                </div>
                {c.items ? (
                  <ul>
                    {c.items.map((item) => (
                      <li key={item}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{c.body}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .gpa-section {
          position: relative;
          background: linear-gradient(180deg, #0b0b0c 0%, #07060a 100%);
          color: #fff;
          padding: 60px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .gpa-bg-orb {
          position: absolute;
          width: 420px;
          height: 420px;
          border-radius: 50%;
          filter: blur(140px);
          pointer-events: none;
          opacity: 0.55;
        }
        .gpa-bg-orb-tl {
          background: rgba(212, 168, 87, 0.22);
          top: -160px;
          left: -120px;
        }
        .gpa-bg-orb-br {
          background: rgba(212, 168, 87, 0.14);
          bottom: -180px;
          right: -120px;
        }
        .gpa-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.45;
        }

        .gpa-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HERO ============ */
        .gpa-hero {
          display: grid;
          grid-template-columns: 1.05fr 1fr;
          gap: 50px;
          align-items: center;
          margin-bottom: 64px;
        }
        .gpa-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 18px;
        }
        .gpa-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gpa-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gpa-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gpa-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 44px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 16px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gpa-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpa-lede {
          font-size: 15.5px;
          line-height: 1.7;
          color: #cfd2d6;
          margin: 0 0 14px;
          max-width: 580px;
        }
        .gpa-meta-line {
          font-size: 12.5px;
          line-height: 1.6;
          color: #9aa0a8;
          margin: 0 0 26px;
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.03);
          border-left: 2px solid rgba(212, 168, 87, 0.5);
          border-radius: 0 8px 8px 0;
        }
        .gpa-meta-line strong { color: #d4a857; font-weight: 700; }

        .gpa-hero-cta-row { display: flex; gap: 12px; flex-wrap: wrap; }
        .gpa-cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 13px 22px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          text-decoration: none;
          box-shadow: 0 8px 22px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gpa-cta-primary:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-2px);
          filter: brightness(1.05);
          box-shadow: 0 12px 28px rgba(212, 168, 87, 0.45);
        }
        .gpa-cta-secondary {
          display: inline-flex;
          align-items: center;
          padding: 13px 22px;
          background: transparent;
          color: #d4a857 !important;
          border: 1px solid rgba(212, 168, 87, 0.4);
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          text-decoration: none;
          transition: background .2s ease, border-color .2s ease;
        }
        .gpa-cta-secondary:hover {
          background: rgba(212, 168, 87, 0.12);
          border-color: #d4a857;
          color: #d4a857 !important;
          text-decoration: none;
        }

        /* HERO VISUAL */
        .gpa-hero-visual {
          position: relative;
        }
        .gpa-hero-image-wrap {
          position: relative;
          border-radius: 22px;
          overflow: hidden;
          background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.3);
          padding: 12px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
        }
        .gpa-hero-image-wrap img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 14px;
          aspect-ratio: 1 / 1;
          object-fit: cover;
        }
        .gpa-hero-image-shine {
          position: absolute;
          inset: 12px;
          border-radius: 14px;
          background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
          pointer-events: none;
        }

        .gpa-hero-tag {
          position: absolute;
          background: rgba(15, 15, 16, 0.92);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(212, 168, 87, 0.35);
          border-radius: 12px;
          padding: 12px 16px;
          box-shadow: 0 10px 22px rgba(0, 0, 0, 0.4);
          z-index: 2;
        }
        .gpa-hero-tag-tl { top: 22px; left: -22px; }
        .gpa-hero-tag-br { bottom: 22px; right: -22px; }
        .gpa-hero-tag-num {
          display: block;
          font-size: 18px;
          font-weight: 700;
          color: #d4a857;
          line-height: 1;
        }
        .gpa-hero-tag-label {
          display: block;
          font-size: 11px;
          color: #b8bcc2;
          letter-spacing: 0.5px;
          margin-top: 3px;
        }

        /* ============ STATS ============ */
        .gpa-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          padding: 24px;
          background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 18px;
          margin-bottom: 64px;
          box-shadow: 0 14px 36px rgba(0, 0, 0, 0.35);
        }
        .gpa-stat {
          text-align: center;
          padding: 8px;
          border-right: 1px solid rgba(212, 168, 87, 0.12);
        }
        .gpa-stat:last-child { border-right: none; }
        .gpa-stat-num {
          font-size: 30px;
          font-weight: 800;
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          line-height: 1;
          letter-spacing: -0.4px;
        }
        .gpa-stat-label {
          margin-top: 6px;
          font-size: 11.5px;
          color: #9aa0a8;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          font-weight: 600;
        }

        /* ============ BLOCK (sections) ============ */
        .gpa-block { margin-bottom: 64px; }
        .gpa-block:last-child { margin-bottom: 0; }
        .gpa-block-head { text-align: center; margin-bottom: 36px; max-width: 720px; margin-left: auto; margin-right: auto; }
        .gpa-block-eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          color: #d4a857;
          letter-spacing: 2.5px;
          margin-bottom: 10px;
        }
        .gpa-block-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 32px;
          color: #fff;
          margin: 0 0 10px;
          font-weight: 700;
          line-height: 1.2;
        }
        .gpa-block-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpa-block-sub {
          color: #9aa0a8;
          font-size: 14px;
          margin: 0;
          line-height: 1.6;
        }

        /* VALUES GRID */
        .gpa-values {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .gpa-value {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 14px;
          padding: 24px 20px;
          transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
        }
        .gpa-value:hover {
          transform: translateY(-4px);
          border-color: rgba(212, 168, 87, 0.5);
          box-shadow: 0 14px 32px rgba(212, 168, 87, 0.12), 0 6px 14px rgba(0, 0, 0, 0.4);
        }
        .gpa-value-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          background: rgba(212, 168, 87, 0.12);
          color: #d4a857;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 14px;
          border: 1px solid rgba(212, 168, 87, 0.25);
        }
        .gpa-value h3 {
          font-size: 15px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gpa-value p {
          font-size: 13px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
        }

        /* TRUST CARDS */
        .gpa-trust-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }
        .gpa-trust-card {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.2);
          border-radius: 16px;
          padding: 26px 24px;
          position: relative;
          overflow: hidden;
          transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
        }
        .gpa-trust-card::before {
          content: "";
          position: absolute;
          top: -60px;
          right: -60px;
          width: 140px;
          height: 140px;
          background: radial-gradient(circle, rgba(212, 168, 87, 0.18), transparent 70%);
          pointer-events: none;
          opacity: 0;
          transition: opacity .3s ease;
        }
        .gpa-trust-card:hover {
          transform: translateY(-4px);
          border-color: rgba(212, 168, 87, 0.5);
          box-shadow: 0 16px 36px rgba(212, 168, 87, 0.14), 0 6px 14px rgba(0, 0, 0, 0.4);
        }
        .gpa-trust-card:hover::before { opacity: 1; }
        .gpa-trust-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          padding-bottom: 14px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gpa-trust-card-head h3 {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 19px;
          color: #fff;
          margin: 0;
          font-weight: 700;
          letter-spacing: -0.2px;
        }
        .gpa-trust-card-hint {
          color: #d4a857;
          font-size: 16px;
          opacity: 0.7;
        }
        .gpa-trust-card ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .gpa-trust-card li {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #cfd2d6;
          font-size: 13.5px;
          line-height: 1.45;
        }
        .gpa-trust-card li svg {
          color: #d4a857;
          flex-shrink: 0;
        }
        .gpa-trust-card p {
          color: #cfd2d6;
          font-size: 13.5px;
          line-height: 1.65;
          margin: 0;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 991px) {
          .gpa-hero { grid-template-columns: 1fr; gap: 36px; }
          .gpa-title { font-size: 36px; }
          .gpa-stats { grid-template-columns: repeat(2, 1fr); padding: 18px; }
          .gpa-stat { border-right: none; }
          .gpa-stat:nth-child(odd) { border-right: 1px solid rgba(212, 168, 87, 0.12); }
          .gpa-values { grid-template-columns: repeat(2, 1fr); }
          .gpa-trust-grid { grid-template-columns: 1fr; }
          .gpa-hero-tag { display: none; }
          .gpa-block-title { font-size: 26px; }
        }
        @media (max-width: 575px) {
          .gpa-section { padding: 44px 14px 60px; }
          .gpa-title { font-size: 26px; }
          .gpa-lede { font-size: 14px; }
          .gpa-stats { grid-template-columns: 1fr 1fr; gap: 0; }
          .gpa-stat { border-right: none !important; padding: 12px 6px; }
          .gpa-stat-num { font-size: 24px; }
          .gpa-values { grid-template-columns: 1fr; }
          .gpa-block-title { font-size: 22px; }
          .gpa-cta-primary, .gpa-cta-secondary {
            flex: 1;
            justify-content: center;
            font-size: 12px;
            padding: 12px 16px;
          }
        }
      `}</style>
    </section>
  );
};

export default About;
